import { useEffect, useState, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { lumi } from './lib/lumi'
import LoginPage from './pages/LoginPage'
import LandingPage from './pages/LandingPage'

// Lazy load heavy pages to split bundle and stay under PWA 2MB limit
const GradeSelectorPage = lazy(() => import('./pages/GradeSelectorPage'))
const AlphabetPracticePage = lazy(() => import('./pages/AlphabetPracticePage'))
const LearningPage = lazy(() => import('./pages/LearningPage'))
const ProgressPage = lazy(() => import('./pages/ProgressPage'))
const WordManagementPage = lazy(() => import('./pages/WordManagementPage'))
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'))
const ParentViewPage = lazy(() => import('./pages/ParentViewPage'))
const AllStudentsPage = lazy(() => import('./pages/AllStudentsPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const SuccessPage = lazy(() => import('./pages/SuccessPage'))
const CancelPage = lazy(() => import('./pages/CancelPage'))
const StudentProgressPage = lazy(() => import('./pages/StudentProgressPage'))
const UserMenuPage = lazy(() => import('./pages/UserMenuPage'))
const PrepositionGradePage = lazy(() => import('./pages/PrepositionGradePage'))
const PrepositionLearningPage = lazy(() => import('./pages/PrepositionLearningPage'))
const PrepositionProgressPage = lazy(() => import('./pages/PrepositionProgressPage'))
const IdiomGradePage = lazy(() => import('./pages/IdiomGradePage'))
const IdiomLearningPage = lazy(() => import('./pages/IdiomLearningPage'))
const IdiomProgressPage = lazy(() => import('./pages/IdiomProgressPage'))
const PhrasalVerbGradePage = lazy(() => import('./pages/PhrasalVerbGradePage'))
const PhrasalVerbLearningPage = lazy(() => import('./pages/PhrasalVerbLearningPage'))
const PhrasalVerbProgressPage = lazy(() => import('./pages/PhrasalVerbProgressPage'))

function PageLoader() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div>
        <div className="text-indigo-600 text-sm">読み込み中...</div>
      </div>
    </div>
  )
}

function App() {
  const [user, setUser] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // URLのm=パラメータを検出してsessionStorageに保存（リダイレクト前に保存）
    const urlParams = new URLSearchParams(window.location.search)
    const managerId = urlParams.get('m')
    if (managerId) {
      sessionStorage.setItem('invite_manager_id', managerId)
      console.log('🔗 招待マネージャーIDを保存:', managerId)
    }

    // Detect LINE browser and redirect to external browser
    const isLineBrowser = /Line/i.test(navigator.userAgent)
    if (isLineBrowser) {
      const currentUrl = window.location.href
      window.location.href = `https://socialplus.jp/?openExternalBrowser=1&url=${encodeURIComponent(currentUrl)}`
      return
    }

    // Service Worker登録はmain.tsxで一元管理（ここでは削除）

    // Check existing auth state
    const currentUser = lumi.auth.user
    setUser(currentUser)
    setLoading(false)

    // Listen to auth changes
    const unsubscribe = lumi.auth.onAuthChange(({ isAuthenticated, user }) => {
      setUser(isAuthenticated ? user : null)
    })

    return () => unsubscribe?.()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-indigo-600 text-lg">読み込み中...</div>
      </div>
    )
  }

  return (
    <BrowserRouter>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <Toaster position="top-center" />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route 
              path="/" 
              element={user ? <Navigate to="/user-menu" replace /> : <LoginPage />} 
            />
            <Route 
              path="/landing" 
              element={<LandingPage />} 
            />
            <Route 
              path="/user-menu" 
              element={user ? <UserMenuPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/grade-select" 
              element={user ? <GradeSelectorPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/alphabet-practice" 
              element={user ? <AlphabetPracticePage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/learning" 
              element={user ? <LearningPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/progress" 
              element={user ? <ProgressPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/manage" 
              element={user ? <WordManagementPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/profile" 
              element={user ? <UserProfilePage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/parent/:token" 
              element={<ParentViewPage />} 
            />
            <Route 
              path="/all-students" 
              element={user ? <AllStudentsPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/pricing" 
              element={user ? <PricingPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/success" 
              element={<SuccessPage />} 
            />
            <Route 
              path="/cancel" 
              element={<CancelPage />} 
            />
            <Route 
              path="/student-progress/:studentId" 
              element={user ? <StudentProgressPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/preposition-grade" 
              element={user ? <PrepositionGradePage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/preposition-learning" 
              element={user ? <PrepositionLearningPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/preposition-progress" 
              element={user ? <PrepositionProgressPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/idiom-grade" 
              element={user ? <IdiomGradePage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/idiom-learning" 
              element={user ? <IdiomLearningPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/idiom-progress" 
              element={user ? <IdiomProgressPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/phrasal-verb-grade" 
              element={user ? <PhrasalVerbGradePage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/phrasal-verb-learning" 
              element={user ? <PhrasalVerbLearningPage /> : <Navigate to="/" replace />} 
            />
            <Route 
              path="/phrasal-verb-progress" 
              element={user ? <PhrasalVerbProgressPage /> : <Navigate to="/" replace />} 
            />
          </Routes>
        </Suspense>
      </div>
    </BrowserRouter>
  )
}

export default App
